import React, { useMemo, useState } from "react";
import {
  Avatar,
  Box,
  Chip,
  Divider,
  IconButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Stack,
  Tooltip,
  Typography,
  useTheme,
} from "@mui/material";
import LogoutIcon from "@mui/icons-material/Logout";
import ManageAccountsIcon from "@mui/icons-material/ManageAccounts";
import LockResetIcon from "@mui/icons-material/LockReset";
import { useKeycloak } from "../KeycloakProvider";

const initialsFor = (name, email) => {
  const source = name || email || "";
  if (!source) return "?";
  const parts = source.split(/[\s@._-]+/).filter(Boolean);
  const first = parts[0]?.[0] || "";
  const second = parts.length > 1 ? parts[1][0] : "";
  return (first + second).toUpperCase() || "?";
};

const UserLogoutWidget = () => {
  const { keycloak } = useKeycloak();
  const theme = useTheme();
  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);

  const token = keycloak?.tokenParsed || {};
  const email = token.email || "";
  const displayName =
    token.name ||
    [token.given_name, token.family_name].filter(Boolean).join(" ") ||
    token.preferred_username ||
    email.split("@")[0] ||
    "User";

  const organization = token.organization || token.org || token.tenant || "";
  const roles = token.realm_access?.roles || token.roles || [];
  const isAdmin = roles.includes("organization-admin");
  const roleLabel = isAdmin ? "Admin" : roles.includes("organization-user") ? "User" : "";

  const avatarBg = theme.palette.secondary.main;
  const avatarFg = theme.palette.getContrastText(avatarBg);
  const initials = useMemo(() => initialsFor(displayName, email), [displayName, email]);

  const accountUrl = useMemo(() => {
    try {
      return keycloak?.createAccountUrl?.();
    } catch {
      return null;
    }
  }, [keycloak]);

  if (!email && !displayName) return null;

  const handleAccount = () => {
    setAnchorEl(null);
    if (accountUrl) window.open(accountUrl, "_blank", "noopener,noreferrer");
  };

  return (
    <>
      <Tooltip title={displayName}>
        <IconButton
          size="small"
          onClick={(e) => setAnchorEl(e.currentTarget)}
          sx={{ p: 0.25 }}
        >
          <Avatar
            sx={{
              width: 32,
              height: 32,
              bgcolor: avatarBg, color: avatarFg,
              fontSize: "0.8rem",
              fontWeight: 700,
            }}
          >
            {initials}
          </Avatar>
        </IconButton>
      </Tooltip>

      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        PaperProps={{ sx: { mt: 1, minWidth: 260, borderRadius: 2 } }}
      >
        <Box sx={{ px: 2, py: 1.5 }}>
          <Stack direction="row" spacing={1.5} alignItems="center">
            <Avatar sx={{ bgcolor: avatarBg, color: avatarFg, width: 40, height: 40, fontWeight: 700 }}>
              {initials}
            </Avatar>
            <Box sx={{ minWidth: 0 }}>
              <Typography variant="body2" fontWeight={600} noWrap>
                {displayName}
              </Typography>
              <Typography variant="caption" color="text.secondary" noWrap sx={{ display: "block" }}>
                {email}
              </Typography>
            </Box>
          </Stack>

          {(organization || roleLabel) && (
            <Stack direction="row" spacing={1} sx={{ mt: 1.5, flexWrap: "wrap", gap: 0.5 }}>
              {organization && (
                <Chip size="small" label={organization} variant="outlined" />
              )}
              {roleLabel && (
                <Chip
                  size="small"
                  label={roleLabel}
                  color={isAdmin ? "primary" : "default"}
                  variant={isAdmin ? "filled" : "outlined"}
                />
              )}
            </Stack>
          )}
        </Box>

        <Divider />

        {accountUrl && (
          <MenuItem onClick={handleAccount}>
            <ListItemIcon>
              <ManageAccountsIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText primary="Account settings" />
          </MenuItem>
        )}

        <MenuItem
          onClick={() => {
            setAnchorEl(null);
            keycloak.login({ action: "UPDATE_PASSWORD" });
          }}
        >
          <ListItemIcon>
            <LockResetIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText primary="Change password" />
        </MenuItem>

        <Divider />

        <MenuItem onClick={() => keycloak.logout()}>
          <ListItemIcon>
            <LogoutIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText primary="Logout" />
        </MenuItem>
      </Menu>
    </>
  );
};

export default UserLogoutWidget;
